import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  CheckCircle,
  Cpu,
  Users,
  CodeSimple,
  ShieldStar,
  MonitorPlay,
  RocketLaunch,
  Sparkle,
  Phone as PhoneIcon,
  Lightning,
  Plug,
} from "@phosphor-icons/react";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";
import {
  TESTIMONIALS,
  SITE,
  PRODUCTS,
  MANAGED_ADDONS,
  VERIFYPRO_CONNECTORS,
  DIGITAL_CHECKS,
} from "@/lib/site";
import { PAGE_SEO, verifyProProductSchema } from "@/lib/seo";

const ADDON_ICONS = { ShieldStar, Users, Cpu, CodeSimple };

const StatusPill = ({ status }) => {
  if (status === "live") {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-[0.2em]">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Live
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold uppercase tracking-[0.2em]">
      Coming soon
    </span>
  );
};

const HomePage = () => {
  const flagship = PRODUCTS.find((p) => p.status === "live");
  const upcoming = PRODUCTS.filter((p) => p.status !== "live");

  return (
    <div data-testid="home-page">
      <Seo page={PAGE_SEO.home} schemas={[verifyProProductSchema()]} />
      {/* Semantic H1 for SEO — visually hidden, screen-reader accessible */}
      <h1 className="sr-only" data-testid="home-h1">Infini10 Technologies — VerifyPro Background Verification SaaS for Indian Enterprises &amp; BFSI</h1>
      <p className="sr-only" data-testid="home-fact-block">{PAGE_SEO.home.h1Fact}</p>
      {/* ============ HERO — PLATFORM POSITIONING ============ */}
      <section
        data-testid="home-hero"
        className="relative bg-[#050815] text-white overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute -top-32 -right-32 h-[640px] w-[640px] rounded-full opacity-25 pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, rgba(254,76,28,0.5), transparent 70%)",
          }}
        />

        <div className="relative max-w-[1100px] mx-auto pb-16 lg:pb-20 text-center">
          <div className="relative fm-fade-up">
            <img
              src="/hero-infinity.png"
              alt="Infinite Vision. 10X Execution. — infinity loop of vision and execution above a mountain peak, with feature pillars Innovate, Accelerate, Collaborate, Impact and Grow Infinitely"
              data-testid="home-hero-image"
              className="relative w-full h-auto object-contain block mx-auto"
              loading="eager"
            />
          </div>

          <div className="mt-6 max-w-3xl mx-auto px-5 fm-fade-up" style={{ animationDelay: "120ms" }}>
            <p
              data-testid="home-hero-copy"
              className="text-lg lg:text-xl text-white/80 leading-relaxed"
            >
              Infini10 Technologies builds modern software that helps businesses work smarter. Every product is <strong className="text-white font-semibold">self-serve</strong>, purpose-built, and designed to deliver value from day one.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/services"
                data-testid="hero-cta-products"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#fe4c1c] text-white text-sm font-semibold hover:bg-[#e63d0e] transition-colors shadow-lg shadow-[#fe4c1c]/30"
              >
                See our products <ArrowUpRight size={18} weight="bold" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-demo"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                <MonitorPlay size={18} weight="bold" /> Book a demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Client marquee moved to About page */}

      {/* ============ FLAGSHIP PRODUCT — VerifyPro ============ */}
      <section
        data-testid="home-flagship"
        className="py-24 lg:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">
              01 · Our flagship product
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1f2a44] leading-[1.02]">
              Meet VerifyPro.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              VerifyPro is our self-serve Background Verification platform — the first product from Infini10 Technologies. Sign up, pick a template, invite candidates, and download audit-grade reports. A modern approach to BGV.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <div className="rounded-3xl bg-[#1f2a44] text-white p-8 lg:p-10 relative overflow-hidden">
                <div
                  aria-hidden
                  className="absolute -bottom-24 -right-24 h-[300px] w-[300px] rounded-full opacity-30"
                  style={{ background: "radial-gradient(closest-side, rgba(254,76,28,0.65), transparent 70%)" }}
                />
                <div className="relative">
                  <div className="flex items-center gap-4">
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#fe4c1c] text-white font-display text-3xl font-bold">
                      {flagship.letter}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-display text-2xl font-bold">{flagship.name}</p>
                        <StatusPill status={flagship.status} />
                      </div>
                      <p className="text-sm text-white/60">{flagship.tagline}</p>
                    </div>
                  </div>
                  <p className="mt-6 text-white/80 leading-relaxed">
                    {flagship.short}
                  </p>
                  <ul className="mt-6 space-y-2.5">
                    {flagship.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-white/85">
                        <CheckCircle size={16} weight="fill" className="text-[#fe4c1c] shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <a
                      href={flagship.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="flagship-cta-launch"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#fe4c1c] text-white text-sm font-semibold hover:bg-[#e63d0e] transition-colors"
                    >
                      Launch VerifyPro <ArrowUpRight size={16} weight="bold" />
                    </a>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
                    >
                      Book a demo
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 lg:pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { title: "Templates", desc: "Banking, Corporate, Startup — one-click deploy." },
                  { title: "Dashboard", desc: "Track every case, live. Filter, comment, adjudicate." },
                  { title: "API & bulk", desc: "CSV, webhooks, REST API for your ATS or HRMS." },
                  { title: "PDF reports", desc: "Tamper-evident, evidence-attached, shareable." },
                  { title: "Team roles", desc: "Analysts, reviewers, approvers. SSO ready." },
                  { title: "DPDP vault", desc: "Encrypted PII. India-resident. Audit-ready." },
                ].map((f) => (
                  <div
                    key={f.title}
                    className="rounded-2xl bg-[#fff6ec] border border-slate-200 p-5 hover:border-[#fe4c1c]/40 hover:shadow-md transition-all"
                  >
                    <CheckCircle size={20} weight="fill" className="text-[#fe4c1c]" />
                    <h4 className="mt-3 font-display text-base font-bold text-[#1f2a44]">{f.title}</h4>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* CONNECTOR STRIP — deep link to Products page for the full grid */}
          <div className="mt-14" data-testid="home-connectors-strip">
            <div className="rounded-3xl bg-[#fff6ec] border border-slate-200 p-6 sm:p-8 lg:p-10">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#fe4c1c]/10 text-[#fe4c1c]">
                    <Plug size={20} weight="duotone" />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">Native connectors</p>
                    <p className="mt-1 font-display text-lg font-bold text-[#1f2a44] leading-tight">
                      Plugs into the HRMS you already run.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {VERIFYPRO_CONNECTORS.map((c) => (
                    <div
                      key={c.name}
                      data-testid={`home-connector-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      title={`${c.name} · ${c.type}`}
                      className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 pr-4 pl-1 py-1 hover:border-[#fe4c1c]/50 hover:shadow-sm transition-all"
                    >
                      <span
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full font-display text-xs font-bold text-white"
                        style={{ backgroundColor: c.color }}
                      >
                        {c.letter}
                      </span>
                      <span className="text-xs font-semibold text-[#1f2a44] whitespace-nowrap">{c.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Digital checks</span>
                  {DIGITAL_CHECKS.map((d) => (
                    <span
                      key={d.name}
                      className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-semibold text-[#1f2a44]"
                    >
                      <span
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full font-display text-[10px] font-bold text-white"
                        style={{ backgroundColor: d.color }}
                      >
                        {d.letter}
                      </span>
                      {d.name}
                    </span>
                  ))}
                </div>
                <Link
                  to="/services#verifypro-deep-dive"
                  data-testid="home-connectors-cta"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#1f2a44] hover:text-[#fe4c1c] hover:gap-3 transition-all self-start sm:self-auto"
                >
                  See all integrations <ArrowRight size={16} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ UPCOMING PRODUCTS ============ */}
      <section
        data-testid="home-upcoming"
        className="py-24 lg:py-32 bg-[#fff6ec] border-y border-[#1f2a44]/10"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">
                02 · The rest of the suite
              </p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1f2a44] leading-[1.05]">
                What&apos;s coming next.
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed max-w-xl">
                Every Infini10 product shares the same design language, the same data model and the same account. Buy one, and the rest slot in naturally.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {upcoming.map((p) => (
              <div
                key={p.slug}
                data-testid={`upcoming-${p.slug}`}
                className="relative rounded-3xl bg-white border border-slate-200 p-8 lg:p-10 hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                <div className="flex items-start gap-4">
                  <span
                    className="inline-flex h-16 w-16 items-center justify-center rounded-2xl font-display text-3xl font-bold text-white shrink-0"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.letter}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-2xl font-bold text-[#1f2a44]">{p.name}</h3>
                      <StatusPill status={p.status} />
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{p.tagline}</p>
                  </div>
                </div>
                <p className="mt-6 text-slate-600 leading-relaxed">{p.short}</p>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0 mt-2" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-7">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#1f2a44] hover:text-[#fe4c1c] hover:gap-3 transition-all"
                  >
                    Get early access <ArrowRight size={16} weight="bold" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY INFINI10 ============ */}
      <section
        data-testid="home-why"
        className="py-24 lg:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">
              03 · The Infini10 way
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1f2a44] leading-[1.05]">
              Product-thinking,<br />not project-billing.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Most B2B software is priced by the seat, sold by the deck and shipped by the consultant. We chose a different road — one where every product is self-serve, opinionated, and designed to be onboarded without a call.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Self-serve from day one",
                "Opinionated defaults",
                "Interoperable by design",
                "Compliance built in, not bolted on",
              ].map((v) => (
                <li key={v} className="flex items-start gap-3 text-sm text-slate-700">
                  <Lightning size={18} weight="fill" className="text-[#fe4c1c] shrink-0 mt-0.5" />
                  {v}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl bg-[#fff6ec] border border-slate-200 p-10 lg:p-12 relative overflow-hidden">
              <div
                aria-hidden
                className="absolute -top-16 -right-16 h-[240px] w-[240px] rounded-full opacity-40"
                style={{ background: "radial-gradient(closest-side, rgba(254,76,28,0.35), transparent 70%)" }}
              />
              <div className="relative">
                <ShieldCheck size={40} weight="duotone" className="text-[#fe4c1c]" />
                <p className="mt-6 font-display text-4xl lg:text-5xl font-bold text-[#1f2a44] leading-[1.05]">
                  Where innovation<br />never ends.
                </p>
                <p className="mt-6 text-sm text-slate-500 uppercase tracking-[0.22em] font-semibold">— The Infini10 product philosophy</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section
        data-testid="home-testimonials"
        className="py-24 lg:py-32 bg-[#fff6ec] border-y border-[#1f2a44]/10"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">
              In their words
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1f2a44]">
              Teams that build on<br />Infini10 products.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, idx) => (
              <figure
                key={idx}
                data-testid={`testimonial-${idx}`}
                className={`rounded-3xl p-8 border ${
                  idx === 1
                    ? "bg-[#1f2a44] text-white border-[#1f2a44]"
                    : "bg-white text-[#1f2a44] border-slate-200"
                }`}
              >
                <p className={`text-2xl leading-snug font-display font-medium ${idx === 1 ? "text-white" : "text-[#1f2a44]"}`}>
                  &ldquo;{t.quote}&rdquo;
                </p>
                <figcaption className={`mt-8 pt-6 border-t ${idx === 1 ? "border-white/15" : "border-slate-200"}`}>
                  <p className="font-semibold">{t.name}</p>
                  <p className={`text-sm ${idx === 1 ? "text-white/60" : "text-slate-500"}`}>{t.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MANAGED SERVICES (ADD-ON) ============ */}
      <section
        data-testid="home-addons"
        className="py-24 lg:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">
                Prefer hands-off?
              </p>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1f2a44] leading-[1.1]">
                Add managed services<br />whenever you need them.
              </h2>
              <p className="mt-5 text-slate-600 max-w-xl leading-relaxed">
                Our products are self-serve by design — but if you&apos;d rather someone else runs them, our team is available on an add-on basis. Billed per case or per month, no long contracts.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="addons-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border-2 border-[#1f2a44] text-[#1f2a44] text-sm font-semibold hover:bg-[#1f2a44] hover:text-white transition-colors self-start lg:self-end"
            >
              Talk to us <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {MANAGED_ADDONS.map((a) => {
              const Icon = ADDON_ICONS[a.icon] || ShieldStar;
              return (
                <div
                  key={a.title}
                  data-testid={`addon-${a.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl bg-[#fff6ec] border border-slate-200 p-6 hover:border-[#1f2a44]/30 hover:shadow-md transition-all"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#fe4c1c]/10 text-[#fe4c1c]">
                    <Icon size={20} weight="duotone" />
                  </span>
                  <h4 className="mt-5 font-display text-lg font-bold text-[#1f2a44]">{a.title}</h4>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a.desc}</p>
                  <p className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#fe4c1c]">
                    Add-on · extra cost
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section
        data-testid="home-final-cta"
        className="py-24 lg:py-28 bg-[#fff6ec]"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="relative rounded-3xl overflow-hidden bg-[#1f2a44] text-white p-10 lg:p-20">
            <div
              aria-hidden
              className="absolute -bottom-24 -left-24 h-[420px] w-[420px] rounded-full opacity-30"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(254,76,28,0.55), transparent 70%)",
              }}
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <span className="fm-tag text-[#fe4c1c] border-[#fe4c1c]/60">
                  <Sparkle size={12} weight="fill" /> Start with VerifyPro
                </span>
                <h2 className="mt-5 font-display text-4xl sm:text-5xl font-bold leading-[1.05]">
                  Try our first product<br />
                  <span className="text-[#fe4c1c]">free for 14 days.</span>
                </h2>
                <p className="mt-5 text-white/75 max-w-lg">
                  No credit card. No sales call. Ten cases on us — see if VerifyPro fits your team.
                </p>
              </div>
              <div className="lg:justify-self-end flex flex-wrap gap-4">
                <a
                  href={SITE.verifyProUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="final-cta-trial"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#fe4c1c] text-white text-sm font-semibold hover:bg-[#e63d0e] transition-colors shadow-lg shadow-[#fe4c1c]/30"
                >
                  Launch VerifyPro <ArrowUpRight size={18} weight="bold" />
                </a>
                <a
                  href={`tel:${SITE.phoneRaw}`}
                  data-testid="final-cta-phone"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
                >
                  <PhoneIcon size={16} weight="bold" /> {SITE.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
